// =============================================================================
// confirmedWrite — the "make sure it actually saved" write helper.
//
// Lifted from the Manchester Kitchen app, which was the ONLY one of the four
// that did this. Front-of-house tills currently fire a save and hope; if the
// backend hiccups, the screen says "done" but nothing landed. This helper makes
// FOH behave like the kitchen: it waits for the backend to confirm and quietly
// rolls the screen back if the save didn't actually take. This is the Phase-0
// "harden FOH writes" job, done once, in one shared place.
//
// Flow:  flip the screen now (optimistic)  ->  POST  ->  check the reply  ->
//        on failure, undo the flip and surface a plain-English error.
//
// Usage:
//   await confirmedWrite({
//     backend, action: "sellOutTimes",
//     payload: { site: SITE.key, sku, soldOutTime },
//     staffName, staffPin,
//     onOptimistic: () => setSoldOut(true),          // flip the UI immediately
//     onRollback:  (msg) => { setSoldOut(false); showToast(msg); },
//     onSuccess:   () => {},                          // usually silent
//   });
//
// Returns { ok, errMsg, data }.
//
// Why the text/plain header: it dodges the CORS "preflight" check that Google
// Apps Script doesn't answer — without it the browser blocks the save outright.
// This is a known Apps-Script quirk, not a style choice. Don't switch it to
// application/json.
// =============================================================================

// -----------------------------------------------------------------------------
// In-flight de-dupe (added 2026-06-27, JJ) — coalesce identical concurrent
// writes so a double-tap on a slow backend can't fire the same save twice and
// create a duplicate row (the bug that put two EOD reports on Trafford 26/06).
// A second call with the same action+payload while the first is still pending
// rides the first request's result instead of starting a new one: same return
// value to the caller, no second network POST, no second optimistic flip.
// Pairs with the backend idempotency guard, which covers the other half — a
// first write that lands but whose response is lost, then retried later.
// -----------------------------------------------------------------------------
const _inFlightWrites = new Map();

export function confirmedWrite(opts = {}) {
  let key = null;
  try { key = (opts.action || "") + "|" + JSON.stringify(opts.payload || {}); } catch (e) { key = null; }
  if (key && _inFlightWrites.has(key)) return _inFlightWrites.get(key);
  const p = _confirmedWriteImpl(opts);
  if (key) {
    _inFlightWrites.set(key, p);
    // Clear once settled so the next genuine submission isn't blocked.
    Promise.resolve(p).then(
      () => _inFlightWrites.delete(key),
      () => _inFlightWrites.delete(key)
    );
  }
  return p;
}

async function _confirmedWriteImpl({
  backend,
  action,
  payload = {},
  staffName,
  staffPin,
  onOptimistic,
  onSuccess,
  onRollback,
  isSuccess, // optional custom check: (parsedReply) => boolean
  // Optional read-back confirmation: async () => boolean. Given when the caller
  // can independently READ whether the write landed (e.g. a checklist tick can
  // re-read the day and look for its slot). Used ONLY for an UNCERTAIN transport
  // outcome — a non-JSON body, a non-2xx, or a thrown POST — where the write may
  // or may not have taken. The write itself stays SINGLE-SHOT (we never re-POST),
  // so a non-idempotent action can't double-write; we read the truth instead.
  // This is the fix for the Meadowhall 2–3 Aug checklist failure: the flaky Apps
  // Script /exec returns an HTML error page with HTTP 200, the old code read that
  // as "saved", and the green tick was a phantom that never wrote a presence tap.
  verify,
  // Injectable for tests; defaults to the real fetch.
  fetchImpl = (u, o) => fetch(u, o),
} = {}) {
  // 1. Optimistic — flip the screen now so staff aren't left staring at a spinner
  //    on a slow backend (Apps Script can take 2–5s).
  if (typeof onOptimistic === "function") onOptimistic();

  let ok = false;
  let errMsg = "";
  let data = null;
  // An UNCERTAIN outcome: the POST neither cleanly succeeded nor was cleanly
  // rejected, so we don't know if it landed. `legacyOk` is the answer for callers
  // that pass no `verify` — kept identical to the old behaviour so untouched
  // callers don't change: a non-JSON body used to count as saved, a non-2xx/throw
  // used to roll back.
  let uncertain = false;
  let legacyOk = false;

  try {
    const res = await fetchImpl(backend, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify({ action, ...payload, staffName, staffPin }),
    });
    if (!res.ok) {
      // A flaky /exec can even 500 AFTER the row is appended, so this is uncertain,
      // not a definite failure — verify if we can.
      errMsg = "Couldn't save (server said HTTP " + res.status + "). Try again.";
      uncertain = true;
    } else {
      // Read the body as TEXT so we can tell a real JSON reply apart from the HTML
      // error/quota page Apps Script serves (with HTTP 200!) when it's overloaded.
      // The old code did res.json().catch(() => null) and then treated null as
      // "saved" — the exact hole behind the phantom green ticks.
      let text = "";
      try { text = await res.text(); } catch (e) { text = ""; }
      let parsed = null, isJson = false;
      if (text) { try { parsed = JSON.parse(text); isJson = true; } catch (e) { isJson = false; } }
      data = parsed;
      const explicitError = isJson && parsed && (parsed.status === "error" || parsed.error);

      if (typeof isSuccess === "function") {
        // Caller knows the exact success shape — trust it (a definite verdict).
        ok = !!isSuccess(parsed);
        if (!ok) errMsg = (parsed && (parsed.error || parsed.message)) || "Save not confirmed.";
      } else if (explicitError) {
        // Backend explicitly rejected it — a re-read would only confirm it isn't
        // there, so this is definite: roll back, never verify.
        errMsg = parsed.error || parsed.message || "The backend rejected the save.";
      } else if (isJson) {
        // A clean JSON reply with no error field counts as saved. (unchanged)
        ok = true;
      } else {
        // Non-JSON / empty body — the flaky /exec case. Don't guess.
        errMsg = "Couldn't confirm the save — try again.";
        uncertain = true;
        legacyOk = true; // old behaviour: a non-JSON body counted as saved
      }
    }
  } catch (e) {
    // Network error — the POST may still have landed while the response was lost.
    errMsg = (e && e.message) || "Couldn't reach the server. Check signal and try again.";
    uncertain = true;
  }

  // For an UNCERTAIN outcome, read the truth back if we were given a verifier;
  // otherwise fall back to the caller's original behaviour.
  if (!ok && uncertain) {
    if (typeof verify === "function") {
      let vok = false;
      try { vok = await verify(); } catch (e) { vok = false; }
      ok = vok;
      if (ok) errMsg = "";
    } else {
      ok = legacyOk;
      if (ok) errMsg = "";
    }
  }

  // 2. Resolve — silent on success, undo the optimistic flip on failure.
  if (ok) {
    if (typeof onSuccess === "function") onSuccess(data);
  } else {
    if (typeof onRollback === "function") onRollback(errMsg, data);
  }

  return { ok, errMsg, data };
}

// -----------------------------------------------------------------------------
// Companion for POLLED LISTS (e.g. the stock-request list the kitchen refreshes
// every few seconds). When you optimistically mark a row done, the next poll can
// briefly show the OLD state before the backend catches up, making the row
// flicker. Stamp an optimistic mark as "fresh" and let it win over the polled
// value until the backend agrees. Lifted from the kitchen's local-mark TTL.
// -----------------------------------------------------------------------------
export const LOCAL_MARK_TTL_MS = 300000; // 5 minutes

export function stampLocalMark(mark) {
  return { ...mark, _ts: (mark && mark._ts) || Date.now() };
}

export function localMarkIsFresh(mark) {
  if (!mark || !mark._ts) return false;
  return Date.now() - mark._ts < LOCAL_MARK_TTL_MS;
}

// -----------------------------------------------------------------------------
// notifyWriteFailed — a dependency-free failure toast. Most FOH screens don't
// have the scanner's showToast in scope, so this injects a small brand-styled
// banner straight into the page (same DOM approach as the version pill / update
// modal). Call it from a confirmedWrite onRollback so a lost save is never
// silent — staff see "didn't save, try again" instead of a false success.
// -----------------------------------------------------------------------------
export function notifyWriteFailed(message) {
  if (typeof document === "undefined") return;
  try {
    const el = document.createElement("div");
    el.setAttribute("role", "alert");
    el.style.cssText =
      "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99990;" +
      "background:#3a1a1a;color:#ff6b6b;border:1px solid rgba(255,107,107,0.45);" +
      "padding:12px 18px;border-radius:10px;font-family:Manrope,sans-serif;font-size:14px;" +
      "font-weight:700;max-width:90vw;box-shadow:0 6px 24px rgba(0,0,0,0.35)";
    el.textContent = "⚠ " + (message || "That didn't save — try again.");
    document.body.appendChild(el);
    setTimeout(() => { try { document.body.removeChild(el); } catch (e) {} }, 6000);
  } catch (e) {}
}
